/**
 * Core HTTP client — auth token, request/response handling, ApiError.
 *
 * Every domain module in this directory builds on `apiClient`; nothing here
 * knows about lessons, billing or any other domain.
 */
import { appEvents } from '../app-events';
import { captureException } from '@/config/observability';
import { supabase } from '@/config/supabase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_VERSION = '/api/v1';

/**
 * Build an absolute API URL (base + version + path).
 *
 * Use this for any request that bypasses the fetch helpers in this module -
 * e.g. the AI SDK chat transport, which needs a fully-qualified URL. A bare
 * relative `/api/v1/...` path resolves against the frontend origin
 * (app.xonera.xyz), which the Vercel SPA rewrite serves as static index.html
 * and rejects non-GET methods with 405. `path` starts with a leading slash,
 * e.g. `/lessons/{id}/conversations/{cid}/chat`.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${API_VERSION}${path}`;
}

// IANA timezone (e.g. "America/Los_Angeles") sent on every request so the
// backend can deliver scheduled notifications in the user's local morning.
// Computed once on module load - the value is stable across a session.
const USER_TIMEZONE =
  (typeof Intl !== 'undefined' &&
    Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  'UTC';

/**
 * API Client Error
 */
class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public data?: any
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

/**
 * Get current Supabase session token
 */
async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session) {
    return null;
  }

  return session.access_token;
}

/**
 * Make authenticated API request
 */
async function request<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Get JWT token from Supabase
  const token = await getAuthToken();

  // A FormData body must NOT carry a hand-set Content-Type: the browser has to
  // generate it so it can include the multipart boundary. Setting it manually
  // produces a body the server cannot parse.
  //
  // Detecting it from the body (rather than taking a flag) is what lets
  // `upload`/`postMultipart` go through this function at all. They previously
  // re-implemented token fetch, headers, URL building and error handling from
  // scratch specifically to dodge this header — and in doing so skipped the
  // 401 refresh-retry, Sentry capture, and every `appEvents` emit, so file
  // uploads had no session-expired banner, no offline banner, no 5xx banner,
  // no budget-exceeded dialog, and 5xx failures never reached Sentry.
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-User-Timezone': USER_TIMEZONE,
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make request
  const url = `${API_BASE_URL}${API_VERSION}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 - Token expired or invalid
    if (response.status === 401) {
      // IMPORTANT: do NOT call supabase.auth.refreshSession() here. It forces
      // a refresh that races Supabase's own autoRefreshToken (and any other
      // concurrent getSession() on a cold load), and because refresh tokens
      // are single-use/rotating that yields "Invalid Refresh Token: Already
      // Used" -> a spurious sign-out. Instead ask getSession() for the token,
      // which Supabase refreshes exactly once under its internal lock, and
      // retry with it.
      const newToken = await getAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });

        if (retryResponse.ok) {
          return retryResponse.json();
        }

        // A non-auth failure on retry is a real error - surface it as-is.
        if (retryResponse.status !== 401) {
          const errorData = await retryResponse.json().catch(() => ({}));
          throw new ApiError(
            retryResponse.status,
            errorData.detail || 'Request failed',
            errorData
          );
        }
      }

      // Still unauthorized (or no token) - the session really is gone. The
      // SessionExpiredBanner subscribes to this event and prompts re-login
      // without losing page state. The throw still fires so callers' onError
      // handlers run (notify.error is silent for 401s by design).
      appEvents.emit({ type: 'session-expired' });
      throw new ApiError(401, 'Session expired. Please login again.');
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const apiErr = new ApiError(
        response.status,
        // Our typed backend errors (guardrail_rejected, budget_exceeded, ...)
        // carry user-facing copy in `message`, not `detail` - fall back to it
        // so callers toast real copy instead of "HTTP 400: Bad Request".
        errorData.detail ||
          errorData.message ||
          `HTTP ${response.status}: ${response.statusText}`,
        errorData
      );
      if (response.status >= 500) {
        captureException(apiErr, { endpoint, status: response.status });
        // The ServiceIssuesBanner / deduped 5xx toast subscribe here so
        // server outages surface once across the whole app instead of
        // every caller having to remember to toast.
        appEvents.emit({
          type: 'server-error',
          status: response.status,
          endpoint,
        });
      } else if (
        response.status === 402 &&
        errorData?.type === 'budget_exceeded'
      ) {
        // BudgetExceededDialog subscribes here. We still throw below so
        // local mutation onError handlers can roll back optimistic UI.
        appEvents.emit({
          type: 'budget-exceeded',
          payload: errorData,
          endpoint,
        });
      }
      throw apiErr;
    }

    // Handle 204 No Content (no response body)
    if (response.status === 204) {
      return null as T;
    }

    // Return successful response
    return response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    console.error('API request failed:', error);
    const networkErr = new ApiError(
      0,
      error instanceof Error ? error.message : 'Network error'
    );
    captureException(networkErr, { endpoint, kind: 'network' });
    // The OfflineBanner subscribes to this so a real fetch failure is
    // visible even when `navigator.onLine` lies (it sometimes claims the
    // device is online while the upstream is unreachable).
    appEvents.emit({ type: 'network-error', endpoint });
    throw networkErr;
  }
}

/**
 * API Client with typed methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get: <T = any>(endpoint: string, options?: RequestInit): Promise<T> => {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post: <T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PUT request
   */
  put: <T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PATCH request
   */
  patch: <T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete: <T = any>(endpoint: string, options?: RequestInit): Promise<T> => {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },

  /**
   * Upload a single file (multipart/form-data).
   *
   * Goes through `request()` like every other verb, so uploads get the 401
   * refresh-retry, the typed-error `message` fallback, Sentry capture on 5xx,
   * and the `session-expired` / `server-error` / `network-error` /
   * `budget-exceeded` events the app-wide banners and the paywall dialog
   * subscribe to. None of that reached uploads while this re-implemented fetch.
   */
  upload: <T = any>(
    endpoint: string,
    file: File,
    fieldName: string = 'file'
  ): Promise<T> => {
    const formData = new FormData();
    formData.append(fieldName, file);

    return request<T>(endpoint, { method: 'POST', body: formData });
  },

  /**
   * POST a pre-built FormData (multipart/form-data).
   * Use for endpoints accepting multiple files + scalar fields together.
   */
  postMultipart: <T = any>(
    endpoint: string,
    formData: FormData
  ): Promise<T> => {
    return request<T>(endpoint, { method: 'POST', body: formData });
  },
};

export { ApiError };
